import { useEffect, useState, useRef, useCallback } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, XCircle, Images, Upload, Download, Link2, ChevronLeft, ChevronRight, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const PAGE = 60;
const MONTHS = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
const monthLabel = (iso) => {
  if (!iso) return "SIN FECHA";
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) + " " + d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
};
const imgUrl = (id, size) => `${API}/gallery/photos/${id}/img?size=${size}`;

const Lightbox = ({ photos, index, setIndex, albums, canManage, onClose, onChanged }) => {
  const p = photos[index];
  const [caption, setCaption] = useState(p?.caption || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { setCaption(p?.caption || ""); setEditing(false); }, [p?.id]);
  const nav = useCallback((d) => setIndex((i) => Math.min(Math.max(i + d, 0), photos.length - 1)), [photos.length, setIndex]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowLeft") nav(-1);
      if (e.key === "ArrowRight") nav(1);
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [nav, onClose]);
  if (!p) return null;

  const saveCaption = async () => {
    try { await api.patch(`/gallery/photos/${p.id}`, { caption }); toast.success("Descripción guardada"); setEditing(false); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const moveAlbum = async (album_id) => {
    try { await api.patch(`/gallery/photos/${p.id}`, { album_id: album_id || "none" }); toast.success("Foto movida"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try { await api.delete(`/gallery/photos/${p.id}`); toast.success("Foto eliminada"); onClose(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col" style={{ background: "rgba(2,4,10,0.95)", backdropFilter: "blur(6px)" }} data-testid="gallery-lightbox">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-mono text-xs text-slate-400">{index + 1} / {photos.length}</span>
        <div className="flex items-center gap-2">
          <a href={imgUrl(p.id, "full")} download data-testid="lightbox-download" className="p-2 rounded-sm hover:bg-white/10 text-slate-300" title="Descargar"><Download className="w-4 h-4" /></a>
          {canManage(p) && <button data-testid="lightbox-delete" onClick={remove} className="p-2 rounded-sm hover:bg-white/10" style={{ color: "var(--red, #ef4444)" }} title="Eliminar"><Trash2 className="w-4 h-4" /></button>}
          <button data-testid="lightbox-close" onClick={onClose} className="p-2 rounded-sm hover:bg-white/10 text-slate-300"><XCircle className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-12">
        <button disabled={index === 0} onClick={() => nav(-1)} data-testid="lightbox-prev"
          className="absolute left-2 p-3 rounded-full hover:bg-white/10 text-slate-200 disabled:opacity-20"><ChevronLeft className="w-7 h-7" /></button>
        <img src={imgUrl(p.id, "full")} alt={p.caption || ""} className="max-h-full max-w-full object-contain rounded-sm" />
        <button disabled={index === photos.length - 1} onClick={() => nav(1)} data-testid="lightbox-next"
          className="absolute right-2 p-3 rounded-full hover:bg-white/10 text-slate-200 disabled:opacity-20"><ChevronRight className="w-7 h-7" /></button>
      </div>
      <div className="px-5 py-3 flex items-center gap-4 flex-wrap" style={{ borderTop: "1px solid var(--border)" }}>
        <div className="flex-1 min-w-[200px]">
          {editing ? (
            <div className="flex gap-2">
              <input value={caption} onChange={(e) => setCaption(e.target.value)} className="input-cyber px-3 py-1.5 text-sm flex-1" placeholder="Descripción de la foto" data-testid="lightbox-caption-input" />
              <button onClick={saveCaption} className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest rounded-sm">Guardar</button>
            </div>
          ) : (
            <div className="text-sm text-slate-200 flex items-center gap-2">
              <span data-testid="lightbox-caption">{p.caption || <span className="text-slate-500">Sin descripción</span>}</span>
              {canManage(p) && <button onClick={() => setEditing(true)} data-testid="lightbox-edit-caption" className="text-slate-500 hover:text-cyan-400"><Pencil className="w-3 h-3" /></button>}
            </div>
          )}
          <div className="text-[10px] text-slate-500 font-mono mt-1">
            {fmtDateTime(p.taken_at)} · Subió: {p.uploaded_by_name}{p.source === "public" ? " (link externo)" : ""}
          </div>
        </div>
        {canManage(p) && albums.length > 0 && (
          <select value={p.album_id || ""} onChange={(e) => moveAlbum(e.target.value)} data-testid="lightbox-album-select"
            className="input-cyber px-2 py-1.5 text-xs">
            <option value="">Sin álbum</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>
    </div>
  );
};

export const ProjectGallery = ({ projectId }) => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const canManage = (p) => user?.role === "admin" || (p.uploaded_by && p.uploaded_by === user?.id);
  const canShare = ["admin", "auxiliar", "residente", "supervisor"].includes(user?.role);

  const loadOverview = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/gallery`);
    setOverview(data);
  }, [projectId]);

  const loadPhotos = useCallback(async (reset = true, skipN = 0) => {
    const params = { limit: PAGE, skip: skipN };
    if (filter !== "all") params.album_id = filter;
    const { data } = await api.get(`/projects/${projectId}/gallery/photos`, { params });
    setPhotos((prev) => reset ? data.items : [...prev, ...data.items]);
    setTotal(data.total);
  }, [projectId, filter]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadPhotos(true, 0); }, [loadPhotos]);
  const refresh = () => { loadOverview(); loadPhotos(true, 0); };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    if (files.length > 20) { toast.error("Máximo 20 fotos por subida"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (filter !== "all" && filter !== "none") fd.append("album_id", filter);
      const { data } = await api.post(`/projects/${projectId}/gallery/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      toast.success(`${data.uploaded} foto(s) subida(s) y comprimida(s)`);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir fotos"); }
    setUploading(false);
  };

  const newAlbum = async () => {
    const name = window.prompt("Nombre del álbum (ej. Cimentación, Estructura, Acabados):");
    if (!name?.trim()) return;
    try { await api.post(`/projects/${projectId}/gallery/albums`, { name }); loadOverview(); toast.success("Álbum creado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const renameAlbum = async (a) => {
    const name = window.prompt("Nuevo nombre del álbum:", a.name);
    if (!name?.trim() || name === a.name) return;
    try { await api.patch(`/gallery/albums/${a.id}`, { name }); loadOverview(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const deleteAlbum = async (a) => {
    if (!window.confirm(`¿Eliminar el álbum "${a.name}"? Las fotos pasarán a "Sin álbum".`)) return;
    try { await api.delete(`/gallery/albums/${a.id}`); if (filter === a.id) setFilter("all"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const shareLink = async () => {
    let url;
    try {
      const { data } = await api.post(`/projects/${projectId}/gallery/share-link`);
      url = `${window.location.origin}/galeria/${data.token}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar el link");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado. Compártelo por WhatsApp con albañiles/herreros para que suban fotos sin cuenta.", { duration: 6000 });
    } catch {
      window.prompt("Copia este link y compártelo con tu gente en obra:", url);
    }
  };
  const downloadZip = () => {
    const albumQ = filter !== "all" ? `?album_id=${filter}` : "";
    api.get(`/projects/${projectId}/gallery/download${albumQ}`, { responseType: "blob", timeout: 300000 })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = "fotos.zip"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("No hay fotos para descargar"));
  };

  const groups = [];
  photos.forEach((p) => {
    const key = monthLabel(p.taken_at);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(p);
    else groups.push({ key, items: [p] });
  });

  return (
    <div className="space-y-4" data-testid="project-gallery">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="gallery-album-chips">
          <button onClick={() => setFilter("all")} data-testid="gallery-chip-all"
            className={`px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold ${filter === "all" ? "btn-primary" : "btn-ghost"}`}
            style={filter !== "all" ? { border: "1px solid var(--border)" } : {}}>
            Todas ({overview?.total_photos ?? 0})
          </button>
          {(overview?.unassigned ?? 0) > 0 && (overview?.albums?.length ?? 0) > 0 && (
            <button onClick={() => setFilter("none")} data-testid="gallery-chip-none"
              className={`px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold ${filter === "none" ? "btn-primary" : "btn-ghost"}`}
              style={filter !== "none" ? { border: "1px solid var(--border)" } : {}}>
              Sin álbum ({overview.unassigned})
            </button>
          )}
          {(overview?.albums || []).map((a) => (
            <span key={a.id} className="inline-flex items-center">
              <button onClick={() => setFilter(a.id)} data-testid={`gallery-chip-${a.id}`}
                className={`px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold ${filter === a.id ? "btn-primary" : "btn-ghost"}`}
                style={filter !== a.id ? { border: "1px solid var(--border)" } : {}}>
                {a.name} ({a.photos_count})
              </button>
              {filter === a.id && (
                <span className="flex ml-0.5">
                  <button onClick={() => renameAlbum(a)} className="p-1 text-slate-500 hover:text-cyan-400" title="Renombrar"><Pencil className="w-3 h-3" /></button>
                  {user?.role === "admin" && <button onClick={() => deleteAlbum(a)} className="p-1 text-slate-500 hover:text-red-400" title="Eliminar álbum"><Trash2 className="w-3 h-3" /></button>}
                </span>
              )}
            </span>
          ))}
          <button onClick={newAlbum} data-testid="gallery-new-album-btn"
            className="px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold btn-ghost flex items-center gap-1"
            style={{ border: "1px dashed var(--border)", color: "var(--cyan)" }}>
            <FolderPlus className="w-3 h-3" /> Álbum
          </button>
        </div>
        <div className="flex items-center gap-2">
          {canShare && (
            <button onClick={shareLink} data-testid="gallery-share-btn" title="Link para que albañiles/herreros suban fotos sin cuenta"
              className="btn-ghost px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5"
              style={{ border: "1px solid #d946ef55", color: "#d946ef" }}>
              <Link2 className="w-3.5 h-3.5" /> Link externo
            </button>
          )}
          <button onClick={downloadZip} data-testid="gallery-download-btn"
            className="btn-ghost px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5"
            style={{ border: "1px solid var(--border)", color: "var(--green)" }}>
            <Download className="w-3.5 h-3.5" /> ZIP
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gallery-upload-btn"
            className="btn-primary px-4 py-2 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-60">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Subiendo…" : "Subir fotos"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="gallery-file-input" />
        </div>
      </div>

      {photos.length === 0 && (
        <div className="panel p-12 text-center text-slate-500" data-testid="gallery-empty">
          <Images className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <div className="text-sm">Sin fotos {filter !== "all" ? "en este álbum" : "todavía"}. Sube las primeras o comparte el link externo con tu gente en obra.</div>
        </div>
      )}

      {groups.map((g) => (
        <div key={g.key}>
          <div className="label-mono text-[10px] mb-2 flex items-center gap-2" style={{ color: "var(--cyan)" }}>
            <span className="h-px flex-1" style={{ background: "var(--border)" }} />{g.key}<span className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {g.items.map((p) => (
              <button key={p.id} onClick={() => setLightbox(photos.indexOf(p))} data-testid={`gallery-photo-${p.id}`}
                className="relative group aspect-square overflow-hidden rounded-sm" style={{ border: "1px solid var(--border)" }}>
                <img src={imgUrl(p.id, "thumb")} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 text-left opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.85))" }}>
                  <div className="text-[9px] text-slate-200 truncate">{p.caption || p.uploaded_by_name}</div>
                  <div className="text-[8px] font-mono text-slate-400">{(p.taken_at || "").slice(0, 10)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {photos.length < total && (
        <div className="text-center">
          <button onClick={() => loadPhotos(false, photos.length)} data-testid="gallery-load-more"
            className="btn-ghost px-4 py-2 rounded-sm text-[10px] uppercase tracking-widest font-semibold" style={{ border: "1px solid var(--border)" }}>
            Cargar más ({photos.length} de {total})
          </button>
        </div>
      )}

      {lightbox !== null && photos[lightbox] && (
        <Lightbox photos={photos} index={lightbox} setIndex={setLightbox} albums={overview?.albums || []}
          canManage={canManage} onClose={() => setLightbox(null)} onChanged={refresh} />
      )}
    </div>
  );
};

export default ProjectGallery;
